import { isClonedVoiceCode } from '@vbee-holding/aivoice-shared-lib';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import moment from 'moment';
import useUserInfo from '@src/hooks/useTtsUser';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { checkFeaturePermission, countTextLength } from '@src/services/tts';
import { useCanUseEOLVoice } from '@src/hooks/useCanUseEOLVoice';
import { checkVietNam } from '@src/utils/checkCountry';
import { FREE_PACKAGE_CODES, PACKAGE_LEVEL } from '@src/constants/package';
import {
  VALID_CHARACTERS_LENGTH_REGEX,
  VALID_CHARACTERS_REGEX,
} from '@src/constants/tts';
import {
  MAX_SPEAKER_FREE_LIMIT,
  MAX_SPEAKERS,
  PROJECT_PRODUCT,
  MAX_TOTAL_CHARACTERS,
  TRANSLATION_STATUS,
} from '@src/constants/project';
import apis from '@src/apis';
import { WARNING_REQUEST_TYPE } from '@src/constants/request';
import sanitize from 'sanitize-html';
import { DIALOG_V2_TYPE } from '@src/constants';
import { useEffect, useRef } from 'react';
import { isBlockChanged } from '@src/services/project';
import { VOICE_PROVIDER } from '@src/constants/voice';
import {
  isUsingTrialVoice,
  willExceedTrialLimit,
} from '@src/services/voiceTrial';
import { formatNameForEOL } from '@src/services/voice';
import usePaywallTracking from '@src/hooks/usePaywallTracking';
import {
  MOE_LOCKED_WALLET,
  MOE_MODULE,
  MOE_PAYWALL_TRIGGER,
  MOE_PAYWALL_TYPE,
} from '@src/constants/moengage';

const useSynthesisValidation = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const { usingPackage, trialVoiceUsage: stateTrialVoiceUsage } = useSelector(
    (state) => state.user,
  );

  const { multiBlock, voiceDefault, audioType, product, translation } =
    useSelector((state) => state.project);
  const speakers = useSelector((state) => state.project.speakers);

  const isTranslationSuccess =
    product === PROJECT_PRODUCT.DUBBING &&
    translation?.status === TRANSLATION_STATUS.SUCCESS;

  const getBlockText = (block) => {
    if (isTranslationSuccess) {
      return block?.translatedText || '';
    }
    return block?.text || '';
  };

  const { ttsUser } = useUserInfo();
  const totalBlockCredits = useRef(0);
  const { getFeatureValue } = useFeatureFlags();
  const { canUseEOLVoice } = useCanUseEOLVoice();
  const voiceHasBeenRenamed = getFeatureValue(
    FEATURE_KEYS.VOICE_HAS_BEEN_RENAMED,
    { userId: user.id, email: user.email, phoneNumber: user.phoneNumber },
  );
  const userVoiceEolEnabled = getFeatureValue(FEATURE_KEYS.USER_VOICE_EOL, {
    userId: user.id,
  });
  const voiceTrialConfig = getFeatureValue(FEATURE_KEYS.TRIAL_VOICE_CONFIG, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
    packageCode: user.packageCode,
  });
  const enableVoiceTrial = Boolean(voiceTrialConfig);
  const speakerConfigEnabled = getFeatureValue(FEATURE_KEYS.SPEAKER_CONFIG, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const usingFreePackage = FREE_PACKAGE_CODES.includes(user?.packageCode);

  const maxSpeakersLimit = usingFreePackage
    ? MAX_SPEAKER_FREE_LIMIT
    : MAX_SPEAKERS;

  const { packageExpiryDate, remainingCharacters, bonusCharacters } =
    user || {};
  const { studio } = ttsUser;
  const { cycle, topUp, custom } = studio || {};
  const {
    remainingCredits: remainingCycleCredits = 0,
    // eslint-disable-next-line prefer-const
    isLocked: isCycleCreditLocked,
  } = cycle || {};
  const {
    remainingCredits: remainingTopUpCredits = 0,
    // eslint-disable-next-line prefer-const
    isLocked: isTopUpCreditLocked,
  } = topUp || {};
  const {
    remainingCredits: remainingCustomCredits = 0,
    // eslint-disable-next-line prefer-const
    isLocked: isCustomCreditLocked,
  } = custom || {};
  // const { ip } = useLocation();
  const { blocks, blockCredits, originalBlocks } = multiBlock || {};

  // Credit calculations

  const getTrialUsageCandidatesForBlocks = (blocksList = []) => {
    if (!enableVoiceTrial || !Array.isArray(blocksList)) return [];

    const voiceCreditsMap = {};

    blocksList.forEach((blockItem) => {
      if (!blockItem?.id) return;
      const voice = blockItem?.voice;
      if (!voice?.code) return;

      const isTrialVoice = isUsingTrialVoice(
        voice,
        voiceTrialConfig,
        ttsUser?.features,
        checkFeaturePermission,
      );

      if (!isTrialVoice) return;

      const text =
        getBlockText(blockItem) || getBlockText(blocks?.[blockItem.id]);
      const characters = countTextLength(text);
      const creditFactor = voice?.creditFactor || 1;
      const credits = characters * creditFactor;

      if (!voiceCreditsMap[voice.code]) {
        voiceCreditsMap[voice.code] = {
          voice,
          credits: 0,
          times: 0,
        };
      }

      voiceCreditsMap[voice.code].credits += credits;
      if (product !== PROJECT_PRODUCT.DUBBING) {
        voiceCreditsMap[voice.code].times += 1;
      } else {
        voiceCreditsMap[voice.code].times = 1;
      }
    });

    return Object.values(voiceCreditsMap);
  };

  const findExceededTrialVoicesForBlocks = (
    blocksList = [],
    usage = stateTrialVoiceUsage,
  ) => {
    if (!enableVoiceTrial) return [];

    const candidates = getTrialUsageCandidatesForBlocks(blocksList);
    if (!candidates.length) return [];

    return candidates.filter(({ voice, credits, times }) =>
      willExceedTrialLimit(voice, voiceTrialConfig, usage, credits, times),
    );
  };

  const checkVoicePermission = (voice) => {
    const isVietNam = checkVietNam();
    const isDefaultGlobalVoice =
      !isVietNam &&
      usingPackage?.level === PACKAGE_LEVEL.BASIC &&
      voice?.code === voiceDefault?.code;

    return (
      !voice?.features?.length ||
      isDefaultGlobalVoice ||
      voice.features.every((item) =>
        checkFeaturePermission(ttsUser.features, item),
      ) ||
      canUseEOLVoice({ voice, user })
    );
  };

  const getTotalTextLength = (targetBlocks = null) => {
    const blocksToCheck = targetBlocks || Object.values(blocks || {});
    return blocksToCheck.reduce((total, block) => {
      const voice = block?.voice || voiceDefault;
      const text = getBlockText(block);
      if (!text) return total;
      return total + countTextLength(text) * (voice?.creditFactor || 1);
    }, 0);
  };
  const getTotalTextLengthWithoutCreditFactor = (targetBlocks = null) => {
    const blocksToCheck = targetBlocks || Object.values(blocks || {});
    return blocksToCheck.reduce((total, block) => {
      const text = getBlockText(block);
      if (!text) return total;
      return total + countTextLength(text);
    }, 0);
  };

  const getInvalidTexts = (targetBlocks = []) => {
    const invalidTexts = targetBlocks.reduce((acc, curr) => {
      const { voice = voiceDefault } = curr;
      const text = getBlockText(curr);
      // Only validate with voice provider is Vbee
      if (voice?.provider !== VOICE_PROVIDER.VBEE) return acc;
      const invalidSentenceText = text.match(VALID_CHARACTERS_LENGTH_REGEX);
      if (invalidSentenceText?.length) return acc?.concat(invalidSentenceText);
      const isValidText = VALID_CHARACTERS_REGEX.test(text);
      if (!isValidText) return acc?.concat([text]);

      return acc;
    }, []);
    return invalidTexts;
  };
  useEffect(() => {
    totalBlockCredits.current = Object.values(blockCredits || {}).reduce(
      (acc, curr) => acc + curr,
      0,
    );
  }, [blockCredits]);

  const { trackHit: trackPaywall } = usePaywallTracking({
    module: MOE_MODULE.PROJECT,
    product,
    trigger: MOE_PAYWALL_TRIGGER.CONVERT,
  });

  const validateSynthesis = (options = {}) => {
    const {
      blockData = null, // Single block data for single block synthesis
      onOpenOutOfCreditsDialog = () => {},
      onOpenExpiredPackageDialog = () => {},
      onOpenDialogLockedCycle = () => {},
      onOpenMaxCharsLimitExceeded = () => {},
      onOpenLimitedFeatureDialog = () => {},
      onSetWarningOneTimeCredits = () => {},
      onOpenWrongSyntaxDialog = () => {},
      onOpenWrongSyntaxDialogV2 = () => {},
      onOpenDiscardedVoiceCloning = () => {},
      onOpenVoiceTrialLimitDialog = () => {},
      onOpenMaxSpeakersLimitDialog = () => {},
    } = options;

    const isChanged = (block, oldBlock) => {
      const withoutExtension = block?.audioLink?.split('.')?.pop();
      const currentBlock = {
        text: getBlockText(block),
        voiceCode: block?.voiceCode,
        speed: block?.speed,
        audioType: withoutExtension,
      };
      const originalBlock = {
        text: getBlockText(oldBlock[block.id]),
        voiceCode: oldBlock[block.id]?.voiceCode,
        speed: oldBlock[block.id]?.speed,
        audioType,
      };
      return isBlockChanged(currentBlock, originalBlock) || !block?.audioLink;
    };

    const newBlocks = Array.isArray(blockData) ? blockData : [blockData];
    // Determine blocks to validate
    const targetBlocks = blockData
      ? newBlocks
      : Object.keys(blocks || {}).map((id) => blocks[id]);
    const blocksWithText = targetBlocks?.filter(
      (block) => !!getBlockText(block) && isChanged(block, originalBlocks),
    );

    const blockWithDiscardedVoiceCloning = blocksWithText.find(
      (block) =>
        isClonedVoiceCode(block?.voice?.code) &&
        block?.voice?.discardAt &&
        moment().isAfter(block?.voice?.discardAt),
    );

    if (blockWithDiscardedVoiceCloning) {
      onOpenDiscardedVoiceCloning();
      return false;
    }

    const blockWithVoiceInactive = blocksWithText.find(
      (block) => !block?.voice?.active && !!block?.voice?.canonicalVoice?.name,
    );
    const blockWithVoiceEOL = blocksWithText.find(
      (block) =>
        block?.voice?.eolDate && !canUseEOLVoice({ voice: block?.voice, user }),
    );
    const isRenameVoice = voiceHasBeenRenamed && blockWithVoiceInactive;

    if (blockWithVoiceEOL) {
      onOpenWrongSyntaxDialogV2({
        name: 'error-syntax',
        variant: 'warning',
        title: t('notice'),
        description: sanitize(
          t(
            userVoiceEolEnabled
              ? 'eolVoiceoverHasBeenDiscontinuedV2'
              : 'eolVoiceoverHasBeenDiscontinued',
            {
              voiceName: formatNameForEOL(
                blockWithVoiceEOL?.voice?.name,
                userVoiceEolEnabled,
              ),
            },
          ),
          {
            allowedTags: ['p', 'strong'],
            allowedAttributes: {
              '*': ['style'],
            },
          },
        ),
        type: DIALOG_V2_TYPE.CANNOT_USE_VOICE,
      });
      return false;
    }

    if (isRenameVoice) {
      onOpenWrongSyntaxDialogV2({
        name: 'error-syntax',
        variant: 'none',
        title: t('notice'),
        description: sanitize(
          t('voiceHasBeenRenamed', {
            newName: blockWithVoiceInactive?.voice?.canonicalVoice?.name,
          }),
          {
            allowedTags: ['strong'],
          },
        ),
        type: DIALOG_V2_TYPE.RENAME_VOICE,
      });
      return false;
    }

    const invalidTexts = getInvalidTexts(blocksWithText);

    if (invalidTexts.length) {
      const invalidText = invalidTexts.join(', ');
      onOpenWrongSyntaxDialog({
        name: 'error-syntax',
        variant: 'warning',
        title: t('errorSyntax'),
        description: t('errorSyntaxDescription'),
        subDescription: t('invalidCharacter', { invalidText }),
      });
      const text = blocksWithText
        .map((sentence) => getBlockText(sentence))
        .join('\n');
      apis.warning.sendWarningInvalidRequest({
        text,
        invalidTexts,
        type: WARNING_REQUEST_TYPE.CHARACTER_EXCEED,
      });

      return false;
    }
    // Check voice permission
    const trialEligibleBlocks = [];
    let unusableVoiceCode;
    const hasUnusableVoice = blocksWithText.some((block) => {
      const voice = block?.voice;
      if (!voice) return false;
      const canUse = checkVoicePermission(voice);
      const isTrialVoice =
        enableVoiceTrial &&
        isUsingTrialVoice(
          voice,
          voiceTrialConfig,
          ttsUser?.features,
          checkFeaturePermission,
        );
      if (isTrialVoice) trialEligibleBlocks.push(block);
      if (canUse || isTrialVoice) return false;
      unusableVoiceCode = voice.code;
      return true;
    });
    if (hasUnusableVoice) {
      // Hit is tracked by LimitedFeaturesDialog itself
      onOpenLimitedFeatureDialog(
        'useThisVoice',
        MOE_PAYWALL_TYPE.LOCKED_VOICE,
        unusableVoiceCode,
      );
      return false;
    }

    if (enableVoiceTrial) {
      const exceededTrialVoices =
        findExceededTrialVoicesForBlocks(trialEligibleBlocks);
      if (exceededTrialVoices.length) {
        onOpenVoiceTrialLimitDialog(
          exceededTrialVoices.map(({ voice }) => voice),
        );
        return false;
      }
    }

    // Check input text length
    const countLength =
      getTotalTextLength(blocksWithText) + totalBlockCredits.current;
    const countLengthWithoutCreditFactor =
      getTotalTextLengthWithoutCreditFactor(blocksWithText) +
      totalBlockCredits.current;

    if (countLengthWithoutCreditFactor > MAX_TOTAL_CHARACTERS) {
      trackPaywall(MOE_PAYWALL_TYPE.MAX_CHARS, {
        characters: countLengthWithoutCreditFactor,
        maxCharacters: MAX_TOTAL_CHARACTERS,
      });
      onOpenMaxCharsLimitExceeded(true);
      return false;
    }

    // Check package expired
    const isExpiredPackage =
      packageExpiryDate && moment().isAfter(packageExpiryDate);

    if (isExpiredPackage) {
      trackPaywall(MOE_PAYWALL_TYPE.EXPIRED_PACKAGE);
      onOpenExpiredPackageDialog(true);
      return false;
    }

    const isLockedCycle =
      isCycleCreditLocked &&
      !remainingTopUpCredits &&
      !remainingCharacters &&
      !bonusCharacters;

    const isLockedTopUp =
      isTopUpCreditLocked && !remainingCharacters && !bonusCharacters;

    if (isLockedCycle || isLockedTopUp) {
      trackPaywall(MOE_PAYWALL_TYPE.LOCKED_CREDITS, {
        lockedWallet: isLockedCycle
          ? MOE_LOCKED_WALLET.CYCLE
          : MOE_LOCKED_WALLET.TOPUP,
      });
      onOpenDialogLockedCycle({
        title: t('charactersTemporarilyLocked'),
        description: t('descriptionPopupLockedCredit'),
      });
      return false;
    }

    const usageRemainingCharacters = remainingCharacters;
    let actualRemainingCycleCredits = remainingCycleCredits;
    let actualRemainingTopUpCredits = remainingTopUpCredits;
    let actualRemainingCustomCredits = remainingCustomCredits;

    if (isCycleCreditLocked) actualRemainingCycleCredits = 0;
    if (isTopUpCreditLocked) actualRemainingTopUpCredits = 0;
    if (isCustomCreditLocked) actualRemainingCustomCredits = 0;

    const totalCredits =
      usageRemainingCharacters +
      bonusCharacters +
      actualRemainingCycleCredits +
      actualRemainingTopUpCredits +
      actualRemainingCustomCredits;

    if (countLength > totalCredits) {
      if (remainingCharacters > 0 && usageRemainingCharacters === 0) {
        onSetWarningOneTimeCredits(true);
      }
      trackPaywall(MOE_PAYWALL_TYPE.OUT_OF_CREDITS, {
        creditsNeeded: countLength,
        creditsRemaining: totalCredits,
        characters: countLengthWithoutCreditFactor,
      });
      onOpenOutOfCreditsDialog(true);
      return false;
    }

    if (speakerConfigEnabled && product === PROJECT_PRODUCT.DUBBING) {
      if (speakers.length > maxSpeakersLimit && usingFreePackage) {
        trackPaywall(MOE_PAYWALL_TYPE.MAX_SPEAKERS, {
          speakers: speakers.length,
          maxSpeakers: maxSpeakersLimit,
        });
        onOpenMaxSpeakersLimitDialog();
        return false;
      }
    }

    return true;
  };

  return {
    validateSynthesis,
    getTotalTextLength,
  };
};

export default useSynthesisValidation;
